/**
 * Wave 29 Chunk 6 — Identity block.
 *
 * Renders the person record attached to an employee/user. Shows:
 *  - Primary (login) email + "internal" / "external" tag
 *  - Personal emails list (private addresses linked to the same person)
 *  - HR-only two-step retirement: Archive → Release email
 *
 * Privacy contract: this block is the ONLY surface where personal emails
 * can be added/removed. Read-only for non-HR users on someone else's
 * profile; in selfMode the user can see (but not edit) their own emails.
 */
import { useEffect, useState } from "react";
import {
  Mail, MailPlus, ShieldCheck, ShieldOff, Loader2, X, AlertTriangle,
  Archive, KeyRound, ChevronRight,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";

export default function IdentityBlock({ userId, selfMode = false, canManage = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyStep, setBusyStep] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      // selfMode: use /me/identity. Otherwise: HR is viewing someone else's profile.
      // We look up the person via the user record's person_id.
      let d;
      if (selfMode) {
        const r = await api.get("/me/identity");
        d = r.data;
      } else {
        // HR viewing someone else's profile — look up the person via the user_id.
        const r = await api.get(`/team/persons/by-user/${userId}`);
        d = r.data;
      }
      setData(d);
    } catch (e) {
      // Quietly fail if person endpoint isn't reachable (non-HR viewer)
      setData({ person: null });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, selfMode]);

  const addEmail = async () => {
    const em = newEmail.trim().toLowerCase();
    if (!em || !em.includes("@")) return toast.error("Enter a valid email.");
    setAdding(true);
    try {
      const { data: p } = await api.post(`/team/persons/${data.person_id}/personal-emails`, { email: em });
      setData({ ...data, person: p });
      setNewEmail("");
      toast.success("Personal email linked.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add email.");
    } finally { setAdding(false); }
  };
  const removeEmail = async (em) => {
    if (!window.confirm(`Unlink ${em} from this person?`)) return;
    try {
      const { data: p } = await api.delete(`/team/persons/${data.person_id}/personal-emails/${encodeURIComponent(em)}`);
      setData({ ...data, person: p });
      toast.success("Removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove email.");
    }
  };
  const archive = async () => {
    if (!window.confirm(
      `Archive this person?\n\nThis disables every login linked to this person. HR data (payslips, attendance, badges, wellness, contracts) stays locked to this person record. After archiving you can release the email so it can be re-assigned to a new hire.`
    )) return;
    setBusyStep("archive");
    try {
      const { data: r } = await api.post(`/team/persons/${data.person_id}/archive`);
      setData({ ...data, person: r.person });
      toast.success("Person archived. Login disabled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed.");
    } finally { setBusyStep(null); }
  };
  const releaseEmail = async () => {
    if (!window.confirm(
      `Release the company email "${data.person.primary_email}"?\n\nThis frees the address so you can onboard a new hire against it. The retired user's login is permanently broken (email renamed). Their HR data stays.`
    )) return;
    setBusyStep("release");
    try {
      await api.post(`/team/persons/${data.person_id}/release-email`, { confirm: true });
      toast.success("Email released. You can now onboard a new hire.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release failed.");
    } finally { setBusyStep(null); }
  };

  if (loading) return <div className="h-24 hv-card animate-pulse" />;
  const p = data?.person;
  if (!p) return null; // No person yet (non-HR viewing someone else, or backfill pending)

  const isInternal = (p.primary_email || "").toLowerCase().endsWith("@hidroverse.co.za");

  return (
    <div className="hv-card p-4 sm:p-5 space-y-3" data-testid="identity-block">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] inline-flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> Identity
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {isInternal ? (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#1FB6C1]/15 text-[#1FB6C1] border border-[#1FB6C1]/40 inline-flex items-center gap-0.5">
              <ShieldCheck className="w-2.5 h-2.5" /> internal
            </span>
          ) : (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/40">
              external
            </span>
          )}
          {p.is_retired && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/40">
              retired
            </span>
          )}
          {p.email_released && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/[0.06] text-[#647087] border border-white/15">
              email released
            </span>
          )}
        </div>
      </div>

      {/* Primary email */}
      <div className="flex items-center gap-2 text-sm">
        <Mail className="w-3.5 h-3.5 text-[#647087]" />
        <span className="text-white" data-testid="identity-primary-email">{p.primary_email || "—"}</span>
        <span className="text-[10px] text-[#647087]">· primary (login)</span>
      </div>

      {/* Personal emails */}
      <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#647087]">
          Personal email{(p.personal_emails || []).length === 1 ? "" : "s"}
          <span className="text-[#475063] normal-case tracking-normal ml-1">— private addresses linked to this person; never recycled</span>
        </div>
        {(p.personal_emails || []).length === 0 && (
          <div className="text-[11px] text-[#647087]" data-testid="identity-no-personal">No personal emails yet.</div>
        )}
        <ul className="space-y-1">
          {(p.personal_emails || []).map((em) => (
            <li key={em} className="flex items-center gap-2 text-xs" data-testid={`identity-personal-${em}`}>
              <Mail className="w-3 h-3 text-[#a0aabf]" />
              <span className="text-white">{em}</span>
              {canManage && !p.is_retired && (
                <button
                  type="button"
                  onClick={() => removeEmail(em)}
                  className="ml-auto text-[#647087] hover:text-red-300"
                  title="Unlink"
                  data-testid={`identity-remove-${em}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
        {canManage && !p.is_retired && (
          <div className="flex items-center gap-1 mt-1">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="personal@example.com"
              className="hv-input text-xs flex-1"
              data-testid="identity-add-input"
            />
            <button
              type="button"
              onClick={addEmail}
              disabled={adding || !newEmail.trim()}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
              data-testid="identity-add-btn"
            >
              {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <MailPlus className="w-3 h-3" />} Add
            </button>
          </div>
        )}
      </div>

      {/* Retirement workflow — HR only */}
      {canManage && (
        <div className="pt-3 border-t border-white/5 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#647087] inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 text-amber-300" /> Retirement workflow
          </div>
          {!p.is_retired ? (
            <button
              type="button"
              onClick={archive}
              disabled={busyStep === "archive"}
              className="hv-btn hv-btn-ghost text-xs text-amber-200 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="identity-archive-btn"
            >
              {busyStep === "archive" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
              Step 1 — Archive person
              <ChevronRight className="w-3 h-3 opacity-50" />
            </button>
          ) : !p.email_released ? (
            <button
              type="button"
              onClick={releaseEmail}
              disabled={busyStep === "release"}
              className="hv-btn hv-btn-ghost text-xs text-red-300 disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="identity-release-btn"
            >
              {busyStep === "release" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
              Step 2 — Release "{p.primary_email}" for re-use
            </button>
          ) : (
            <div className="text-[11px] text-[#647087] inline-flex items-center gap-1">
              <ShieldOff className="w-3 h-3" /> Email released on {new Date(p.released_at || p.retired_at).toLocaleDateString("en-ZA")}.
              You can now onboard a new hire against {p.released_email_from || p.primary_email}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
